import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  computed,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VideoService, Video } from '../../core/services/video';

@Component({
  selector: 'app-video-library',
  imports: [FormsModule],
  templateUrl: './video-library.html',
  styleUrl: './video-library.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoLibraryComponent implements OnInit, OnDestroy {
  private videoService = inject(VideoService);

  // ─── List state ────────────────────────────────────────────────────────────
  videos = signal<Video[]>([]);
  total = signal(0);
  isLoading = signal(false);
  errorMsg = signal<string | null>(null);
  searchQuery = '';
  skip = signal(0);
  readonly limit = 48;

  hasMore = computed(() => this.skip() + this.limit < this.total());

  // ─── Add video state ───────────────────────────────────────────────────────
  showAddPanel = signal(false);
  addClosing = signal(false);
  private addCloseTimer: ReturnType<typeof setTimeout> | null = null;
  addUrl = '';
  addTitle = '';
  addTags = '';
  isAdding = signal(false);
  addError = signal<string | null>(null);

  // ─── Edit state ────────────────────────────────────────────────────────────
  editingVideo = signal<Video | null>(null);
  editTitle = '';
  editTags = '';
  isSavingEdit = signal(false);
  editError = signal<string | null>(null);

  // ─── Delete state ──────────────────────────────────────────────────────────
  videoToDelete = signal<Video | null>(null);
  isDeleting = signal(false);

  // ─── Thumbnail actions ─────────────────────────────────────────────────────
  isGeneratingThumb = signal<Set<string>>(new Set());
  isResettingThumb = signal<Set<string>>(new Set());

  // ─── Set thumbnail URL ─────────────────────────────────────────────────────
  thumbVideo = signal<Video | null>(null);
  thumbUrl = '';
  thumbPreviewUrl = signal<string | null>(null);
  isSavingThumb = signal(false);
  thumbError = signal<string | null>(null);

  // ─── Batch select ──────────────────────────────────────────────────────────
  selectedIds = signal<Set<string>>(new Set());
  isBatchDeleting = signal(false);
  selectedCount = computed(() => this.selectedIds().size);
  allSelected = computed(() => {
    const vids = this.videos();
    return vids.length > 0 && this.selectedIds().size === vids.length;
  });

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.addCloseTimer) clearTimeout(this.addCloseTimer);
  }

  // ─── Load ───────────────────────────────────────────────────────────────────

  load(append = false): void {
    this.isLoading.set(true);
    this.errorMsg.set(null);
    this.videoService.list(this.skip(), this.limit, this.searchQuery || undefined).subscribe({
      next: (res) => {
        this.videos.set(append ? [...this.videos(), ...res.items] : res.items);
        this.total.set(res.total);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.errorMsg.set(err?.error?.detail || 'Failed to load videos.');
        this.isLoading.set(false);
      },
    });
  }

  onSearch(): void {
    this.skip.set(0);
    this.selectedIds.set(new Set());
    this.load();
  }

  loadMore(): void {
    this.skip.update(s => s + this.limit);
    this.load(true);
  }

  // ─── Add ────────────────────────────────────────────────────────────────────

  openAddPanel(): void {
    if (this.addCloseTimer) {
      clearTimeout(this.addCloseTimer);
      this.addCloseTimer = null;
    }
    this.addClosing.set(false);
    this.addUrl = '';
    this.addTitle = '';
    this.addTags = '';
    this.addError.set(null);
    this.showAddPanel.set(true);
  }

  closeAddPanel(): void {
    if (this.isAdding() || !this.showAddPanel() || this.addClosing()) return;
    this.addClosing.set(true);
    this.addCloseTimer = setTimeout(() => {
      this.showAddPanel.set(false);
      this.addClosing.set(false);
      this.addCloseTimer = null;
    }, 200);
  }

  submitAdd(): void {
    const url = this.addUrl.trim();
    if (!url) {
      this.addError.set('Please enter a YouTube URL.');
      return;
    }
    if (this.isAdding()) return;

    const tags = this.addTags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    this.isAdding.set(true);
    this.addError.set(null);
    this.videoService.add({
      youtube_url: url,
      title: this.addTitle.trim() || undefined,
      tags,
    }).subscribe({
      next: (video) => {
        this.videos.update(list => [video, ...list]);
        this.total.update(t => t + 1);
        this.isAdding.set(false);
        this.closeAddPanel();
      },
      error: (err) => {
        this.addError.set(err?.error?.detail || 'Failed to add video.');
        this.isAdding.set(false);
      },
    });
  }

  // ─── Edit ───────────────────────────────────────────────────────────────────

  openEdit(video: Video): void {
    this.editingVideo.set(video);
    this.editTitle = video.title;
    this.editTags = video.tags.join(', ');
    this.editError.set(null);
    this.isSavingEdit.set(false);
  }

  closeEdit(): void {
    if (this.isSavingEdit()) return;
    this.editingVideo.set(null);
  }

  saveEdit(): void {
    const video = this.editingVideo();
    if (!video || this.isSavingEdit()) return;
    const title = this.editTitle.trim();
    if (!title) {
      this.editError.set('Title cannot be empty.');
      return;
    }
    const tags = this.editTags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    this.isSavingEdit.set(true);
    this.editError.set(null);
    this.videoService.update(video.id, { title, tags }).subscribe({
      next: (updated) => {
        this.videos.update(list => list.map(v => v.id === updated.id ? updated : v));
        this.isSavingEdit.set(false);
        this.editingVideo.set(null);
      },
      error: (err) => {
        this.editError.set(err?.error?.detail || 'Save failed.');
        this.isSavingEdit.set(false);
      },
    });
  }

  // ─── Delete ─────────────────────────────────────────────────────────────────

  openDelete(video: Video): void {
    this.videoToDelete.set(video);
  }

  closeDelete(): void {
    if (this.isDeleting()) return;
    this.videoToDelete.set(null);
  }

  confirmDelete(): void {
    const video = this.videoToDelete();
    if (!video || this.isDeleting()) return;
    this.isDeleting.set(true);
    this.videoService.delete(video.id).subscribe({
      next: () => {
        this.videos.update(list => list.filter(v => v.id !== video.id));
        this.total.update(t => Math.max(0, t - 1));
        this.selectedIds.update(set => { const s = new Set(set); s.delete(video.id); return s; });
        this.isDeleting.set(false);
        this.videoToDelete.set(null);
      },
      error: (err) => {
        this.errorMsg.set(err?.error?.detail || 'Delete failed.');
        this.isDeleting.set(false);
        this.videoToDelete.set(null);
      },
    });
  }

  // ─── Batch select / delete ──────────────────────────────────────────────────

  toggleSelect(id: string): void {
    this.selectedIds.update(set => {
      const s = new Set(set);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  toggleSelectAll(): void {
    const all = this.videos().map(v => v.id);
    if (this.selectedIds().size === all.length) {
      this.selectedIds.set(new Set());
    } else {
      this.selectedIds.set(new Set(all));
    }
  }

  batchDelete(): void {
    const ids = Array.from(this.selectedIds());
    if (!ids.length || this.isBatchDeleting()) return;
    this.isBatchDeleting.set(true);
    this.videoService.batchDelete(ids).subscribe({
      next: () => {
        this.videos.update(list => list.filter(v => !ids.includes(v.id)));
        this.total.update(t => Math.max(0, t - ids.length));
        this.selectedIds.set(new Set());
        this.isBatchDeleting.set(false);
      },
      error: (err) => {
        this.errorMsg.set(err?.error?.detail || 'Batch delete failed.');
        this.isBatchDeleting.set(false);
      },
    });
  }

  // ─── Set thumbnail URL ─────────────────────────────────────────────────────

  openSetThumb(video: Video): void {
    this.thumbVideo.set(video);
    this.thumbUrl = video.thumbnail_url;
    this.thumbPreviewUrl.set(video.thumbnail_url);
    this.thumbError.set(null);
    this.isSavingThumb.set(false);
  }

  closeSetThumb(): void {
    if (this.isSavingThumb()) return;
    this.thumbVideo.set(null);
  }

  onThumbUrlChange(): void {
    const url = this.thumbUrl.trim();
    this.thumbPreviewUrl.set(url || null);
  }

  saveThumb(): void {
    const video = this.thumbVideo();
    const url = this.thumbUrl.trim();
    if (!video || !url || this.isSavingThumb()) return;
    this.isSavingThumb.set(true);
    this.thumbError.set(null);
    this.videoService.setThumbnailUrl(video.id, url).subscribe({
      next: (updated) => {
        this.videos.update(list => list.map(v => v.id === updated.id ? updated : v));
        this.isSavingThumb.set(false);
        this.thumbVideo.set(null);
      },
      error: (err) => {
        this.thumbError.set(err?.error?.detail || 'Failed to update thumbnail.');
        this.isSavingThumb.set(false);
      },
    });
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  youtubeWatchUrl(video: Video): string {
    return video.youtube_url;
  }

  embedUrl(video: Video): string {
    return `https://www.youtube.com/embed/${video.video_id}`;
  }

  // ─── Thumbnail actions ─────────────────────────────────────────────────────

  generatingFor(id: string): boolean {
    return this.isGeneratingThumb().has(id);
  }

  resettingFor(id: string): boolean {
    return this.isResettingThumb().has(id);
  }

  generateThumbnail(video: Video): void {
    if (this.generatingFor(video.id)) return;
    this.errorMsg.set(null);
    this.isGeneratingThumb.update(set => new Set(set).add(video.id));
    this.videoService.generateThumbnail(video.id).subscribe({
      next: (updated) => {
        this.videos.update(list => list.map(v => v.id === updated.id ? updated : v));
        this.isGeneratingThumb.update(set => { const s = new Set(set); s.delete(video.id); return s; });
      },
      error: (err) => {
        this.errorMsg.set(err?.error?.detail || 'Thumbnail generation failed.');
        this.isGeneratingThumb.update(set => { const s = new Set(set); s.delete(video.id); return s; });
      },
    });
  }

  resetThumbnail(video: Video): void {
    if (this.resettingFor(video.id)) return;
    this.errorMsg.set(null);
    this.isResettingThumb.update(set => new Set(set).add(video.id));
    this.videoService.resetThumbnail(video.id).subscribe({
      next: (updated) => {
        this.videos.update(list => list.map(v => v.id === updated.id ? updated : v));
        this.isResettingThumb.update(set => { const s = new Set(set); s.delete(video.id); return s; });
      },
      error: (err) => {
        this.errorMsg.set(err?.error?.detail || 'Thumbnail reset failed.');
        this.isResettingThumb.update(set => { const s = new Set(set); s.delete(video.id); return s; });
      },
    });
  }
}
